import React, { useState, useEffect, useMemo } from "react";
import { Button } from "../components/ui/button";
import {
  Activity,
  Loader2,
  User,
  Calendar,
  CalendarDays,
  Search,
  Download,
  ChevronDown,
  Users,
  Check,
  Youtube,
  AlertCircle,
  Monitor,
  Globe,
  Hourglass,
  Clock,
  AppWindow,
  Timer,
  BarChart3,
  ChevronRight,
  X,
  Folder,
  FileText,
  Music,
  Image,
  Video,
  Wifi,
  Zap,
  Gauge,
  Sparkles,
  ClipboardList,
  Coffee,
  ListChecks,
  CheckCircle2,
  Circle,
  Plus,
  Shuffle,
} from "lucide-react";
import { format } from "date-fns";
import { useToast } from "../hooks/useToast";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import api from "../api/api";
import { getEmployees, getMyEmployeeProfile } from "../api/employees";
import { useAccess } from "../contexts/AccessContext";
import {
  classifyActivity,
  summarizeSession,
  isBrowserApp,
  CATEGORY_META,
  type Category,
} from "../utils/productivity";

/* ─── Types ─────────────────────────────────────────────── */
interface AppUsage {
  app_name: string;
  duration_mins: number;
}
interface MediaDetected {
  url?: string;
  app_name?: string;
  end_time?: string;
  start_time?: string;
  media_title?: string;
  duration_mins?: number;
}
interface IdlePeriod {
  type: string;
  end_time: string;
  start_time: string;
  duration_mins: number;
}
interface TimelineEvent {
  url: string | null;
  time: string;
  type: string;
  event: string | null;
  label: string | null;
  media: any | null;
  title: string | null;
  app_exe: string | null;
  app_name: string | null;
  end_time: string;
  duration_mins: number;
  file_path?: string | null;
}

/* ─── NEW: Remote Session Type ───────────────────────────── */
interface RemoteSession {
  app: string;
  time: string;
  event: string;
  end_time: string;
  ocr_text?: string;
  ocr_urls?: string[];
  ocr_paths?: string[];
  machine_type?: string;
  duration_mins: number;
  user_activity?: Record<string, any>;
}

interface ActivityData {
  id: string;
  employeeID: string;
  employee_name: string;
  date: string;
  first_activity: string;
  last_activity: string;
  total_tracked_mins: number;
  active_mins: number;
  idle_mins: number;
  shutdown_mins: number;
  app_usage: AppUsage[];
  browser_usage: any[];
  media_detected: MediaDetected[];
  idle_periods: IdlePeriod[];
  timeline: TimelineEvent[];
  full_summary: any[];
  remote_sessions?: RemoteSession[]; // NEW
  user_id: string;
  headOfficeID: string;
  masterID: string;
  branchID: string;
  level: number;
  generated_at: string | null;
  created_at: string | null;
  last_updated: string | null;
  updated_at: string;
}
interface ApiResponse {
  success: boolean;
  total: number;
  returned: number;
  limit: number;
  offset: number;
  monthly_summary?: {
    total_active_mins: number;
    total_idle_mins: number;
    total_tracked_mins: number;
    total_active_hrs: number;
    total_idle_hrs: number;
    total_tracked_hrs: number;
  };
  data: ActivityData[];
}
interface Employee {
  employeeID: string;
  employee_name: string;
  user_id?: string;
  first_name?: string;
  last_name?: string;
}
interface EmployeeSummary {
  employeeID: string;
  employee_name: string;
  total_tracked_mins: number;
  active_mins: number;
  idle_mins: number;
  data: ActivityData[];
}
interface BrowserSite {
  key: string;
  url: string;
  duration_mins: number;
  title: string;
  visits: number;
}
interface MediaItem {
  id: string;
  title: string;
  url: string;
  app_name: string;
  start_time: string;
  end_time: string;
  duration_mins: number;
  fmt_start: string;
  fmt_end: string;
  fmt_dur: string;
}
/* ─── Apps to hide from display ─────────────────────────── */
const HIDDEN_APPS = ["DLP-FileTracker", "DLP-Clipboard", "Mstsc", "Remote Desktop (RDP)", "mstsc", "mstsc.exe"];

/* ─── Helpers ────────────────────────────────────────────── */
const fmtTime = (dt: string) => {
  if (!dt) return "—";
  try {
    // Handle "2026-04-29 10:04:17" format (space separator) and ISO format
    const normalized = dt.replace(" ", "T");
    return format(new Date(normalized), "HH:mm:ss");
  } catch {
    return dt;
  }
};
const fmtDate = (d: string) => {
  try {
    return format(new Date(d), "MMM dd, yyyy");
  } catch {
    return d;
  }
};
const fmtMins = (m: number) => {
  const hours = Math.floor(m / 60);
  const minutes = Math.round(m % 60);
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
};
const fmtDur = (m: number) => {
  if (!m) return "0:00";
  const mins = Math.floor(m);
  const secs = Math.floor((m - mins) * 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const truncateText = (text: string, maxLength: number = 60) => {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
};

const formatFilePath = (path: string) => {
  if (!path) return "";
  return path;
};

const APP_COLORS: Record<string, string> = {
  "Whatsapp.root": "var(--gt-brand-whatsapp)",
  Chrome: "var(--gt-brand-chrome)",
  Explorer: "var(--gt-brand-explorer)",
  Acrobat: "var(--gt-brand-acrobat)",
  "Excel.exe": "var(--gt-brand-excel)",
  "Winword.exe": "var(--gt-brand-word)",
  Workmoniter: "var(--gt-indigo-500)",
  Photos: "var(--gt-brand-photos)",
  Notepad: "var(--gt-brand-neutral)",
};
const appColor = (n: string) => APP_COLORS[n] || "var(--gt-heat-4)";

const getAppIcon = (appName: string) => {
  const name = appName?.toLowerCase() || "";
  if (name.includes("whatsapp")) return "💬";
  if (name.includes("chrome")) return "🌐";
  if (name.includes("brave")) return "🦁";
  if (name.includes("explorer")) return "📁";
  if (name.includes("acrobat") || name.includes("pdf")) return "📄";
  if (name.includes("excel")) return "📊";
  if (name.includes("word")) return "📝";
  if (name.includes("photo")) return "🖼️";
  if (name.includes("terminal") || name.includes("cmd")) return "💻";
  if (name.includes("code")) return "👨‍💻";
  return "📱";
};

const getMediaIcon = (appName: string) => {
  const name = appName?.toLowerCase() || "";
  if (name.includes("youtube")) return "▶️";
  if (name.includes("spotify")) return "🎵";
  if (name.includes("netflix")) return "🎬";
  if (name.includes("prime")) return "📺";
  if (name.includes("hotstar")) return "🏏";
  return "🎥";
};

const getDisplayNameFromUrl = (url: string): string => {
  if (!url) return "";
  try {
    let cleanUrl = url.replace(/^https?:\/\//, "").replace(/^www\./, "");
    const domain = cleanUrl.split("/")[0];
    return domain;
  } catch {
    return url;
  }
};

const isFilePath = (path: string): boolean => {
  if (!path) return false;
  const fileExtensions = [
    ".pdf", ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx", ".txt",
    ".jpg", ".jpeg", ".png", ".gif", ".mp4", ".mp3", ".zip", ".rar",
    ".tmp", ".py", ".js", ".html", ".css", ".json", ".xml", ".log",
  ];
  return (
    fileExtensions.some((ext) => path.toLowerCase().includes(ext)) ||
    path.includes("/uploads/") ||
    path.includes("/files/") ||
    path.includes("/documents/") ||
    path.includes("C:\\") ||
    path.includes(":\\") ||
    path.startsWith("file://")
  );
};

const getFileIcon = (path: string) => {
  if (!path) return <FileText style={{ width: 12, height: 12 }} />;
  const lowerPath = path.toLowerCase();
  if (lowerPath.includes(".pdf")) return <FileText style={{ width: 12, height: 12 }} />;
  if (lowerPath.includes(".doc") || lowerPath.includes(".docx")) return <FileText style={{ width: 12, height: 12 }} />;
  if (lowerPath.includes(".xls") || lowerPath.includes(".xlsx")) return <FileText style={{ width: 12, height: 12 }} />;
  if (lowerPath.includes(".jpg") || lowerPath.includes(".jpeg") || lowerPath.includes(".png") || lowerPath.includes(".gif"))
    return <Image style={{ width: 12, height: 12 }} />;
  if (lowerPath.includes(".mp3") || lowerPath.includes(".wav")) return <Music style={{ width: 12, height: 12 }} />;
  if (lowerPath.includes(".mp4") || lowerPath.includes(".avi") || lowerPath.includes(".mkv"))
    return <Video style={{ width: 12, height: 12 }} />;
  if (lowerPath.includes(".py")) return <FileText style={{ width: 12, height: 12 }} />;
  if (lowerPath.includes(".js")) return <FileText style={{ width: 12, height: 12 }} />;
  return <FileText style={{ width: 12, height: 12 }} />;
};

const getFileName = (path: string): string => {
  if (!path) return "";
  const parts = path.split(/[/\\]/);
  return parts[parts.length - 1];
};

const ClickablePath: React.FC<{ path: string; children?: React.ReactNode }> = ({ path, children }) => {
  if (!path) return <span style={{ fontSize: 11, color: "var(--gt-text-tertiary)" }}>No path available</span>;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (path.match(/^[A-Za-z]:\\/)) {
      const fileUrl = `file:///${path.replace(/\\/g, "/")}`;
      window.open(fileUrl, "_blank");
    } else if (path.startsWith("file://")) {
      window.open(path, "_blank");
    } else if (path.startsWith("http://") || path.startsWith("https://")) {
      window.open(path, "_blank", "noopener,noreferrer");
    } else {
      window.open(path, "_blank");
    }
  };

  const isFile = isFilePath(path);
  const displayText = children || formatFilePath(path);

  return (
    <a
      href="#"
      onClick={handleClick}
      style={{
        fontSize: 11,
        color: "#3b82f6",
        wordBreak: "break-all",
        textDecoration: "none",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        flexWrap: "wrap",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
      onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
    >
      {isFile && getFileIcon(path)}
      <span style={{ fontFamily: "monospace", fontSize: 10 }}>{displayText}</span>
      <span style={{ fontSize: 9, opacity: 0.7 }}>🔗</span>
    </a>
  );
};

/* ─── Chip Component ───────────────────────────────────── */
const Chip: React.FC<{ label: string; value: string; color?: string }> = ({
  label, value, color = "var(--gt-text-primary)",
}) => (
  <div style={{ background: "var(--gt-surface-1)", border: "1px solid var(--gt-border)", borderRadius: 12, padding: "7px 13px", textAlign: "center", minWidth: 56 }}>
    <p style={{ fontSize: 9, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--gt-text-tertiary)", lineHeight: 1, marginBottom: 5 }}>{label}</p>
    <p style={{ fontSize: 13, fontWeight: 700, lineHeight: 1, color, fontVariantNumeric: "tabular-nums" }}>{value}</p>
  </div>
);

/* ─── Productivity Badge ───────────────────────────────── */
const ProductivityBadge: React.FC<{ cat: Category }> = ({ cat }) => {
  const m = CATEGORY_META[cat];
  return (
    <span
      title={m.label}
      style={{
        fontSize: 9,
        fontWeight: 700,
        padding: "2px 7px",
        borderRadius: 20,
        background: m.bg,
        color: m.fg,
        whiteSpace: "nowrap",
        flexShrink: 0,
        letterSpacing: "0.02em",
      }}
    >
      {m.label}
    </span>
  );
};

/* ─── AI Summary types ──────────────────────────────────── */
interface RoleAdjustedTotals {
  score: number;
  productive_mins: number;
  neutral_mins: number;
  unproductive_mins: number;
}
/** The five narrative sections the model returns, kept separate so the panel
 *  can render each with its own heading/icon instead of one wall of text. */
interface AiSections {
  highlights?: string[]; // 3-4 short bullet points (combined overview + rhythm)
  specific_activity?: string;
  breaks_and_attention?: string;
  tasks_and_planning?: string;
}
/** One task/planner item, as returned by the backend task-summary lookup. */
interface AiTaskItem {
  title: string;
  kind: string; // "task" (shared) | "todo" (personal planner)
  priority: string;
  status: string;
}
interface AiTaskSummary {
  completed: AiTaskItem[];
  created: AiTaskItem[];
  due: AiTaskItem[];
  has_any: boolean;
}
/** Top apps + their window/site detail — used to render the structured
 *  "what they worked on" section instead of the model's prose. */
interface AiAppActivity {
  app_name: string;
  total_mins: number;
  category: Category;
  windows: Array<{ title: string; mins: number; url?: string }>;
}
interface AiSummaryResponse {
  ok: boolean;
  summary?: string;
  sections?: AiSections | null;
  tasks?: AiTaskSummary | null;
  source?: string; // "gemini" | "groq" | "openai" | whichever provider served it
  message?: string;
  code?: string;
  role_title?: string | null;
  role_adjusted?: RoleAdjustedTotals | null;
}

/* ─── Per-app window/tab titles, for whichever app is asked about ───
   Not Chrome/VS-Code-specific — works for any app_name present in the
   timeline. Groups by exact window title and sums time spent on it. */
interface WindowTitleUsage {
  title: string;
  mins: number;
  visits: number;
}
const getAppWindowTitles = (timeline: TimelineEvent[], appName: string, limit = 8): WindowTitleUsage[] => {
  const map = new Map<string, WindowTitleUsage>();
  for (const ev of timeline) {
    if (!ev.title || (ev.app_name || "").toLowerCase() !== appName.toLowerCase()) continue;
    const key = ev.title.trim();
    if (!key) continue;
    const cur = map.get(key) || { title: key, mins: 0, visits: 0 };
    cur.mins += ev.duration_mins || 0;
    cur.visits += 1;
    map.set(key, cur);
  }
  return [...map.values()].sort((a, b) => b.mins - a.mins).slice(0, limit);
};

/* ─── Chronological flow — the only signal that carries sequence/timing ───
   Merges consecutive timeline events that share the same app + window title
   into one block (collapses the tracker's periodic re-sampling of a window
   that's still open), then drops sub-minute flicker and caps the count —
   keeping the day's real rhythm without shipping the raw 100s-of-events log. */
interface FlowBlock {
  app_name: string;
  title?: string;
  start: string;
  end: string;
  mins: number;
}
const buildTimelineFlow = (timeline: TimelineEvent[], capCount = 50, minMins = 1): { blocks: FlowBlock[]; totalSwitches: number } => {
  const sorted = [...timeline]
    .filter((e) => e.app_name && e.time)
    .sort((a, b) => a.time.localeCompare(b.time));

  const blocks: FlowBlock[] = [];
  for (const ev of sorted) {
    const last = blocks[blocks.length - 1];
    const sameWindow = last && last.app_name.toLowerCase() === (ev.app_name || "").toLowerCase() && (last.title || "") === (ev.title || "");
    if (sameWindow) {
      last.end = ev.end_time || ev.time;
      last.mins = Math.round((last.mins + (ev.duration_mins || 0)) * 10) / 10;
    } else {
      blocks.push({
        app_name: ev.app_name!,
        title: ev.title || undefined,
        start: ev.time,
        end: ev.end_time || ev.time,
        mins: Math.round((ev.duration_mins || 0) * 10) / 10,
      });
    }
  }

  const totalSwitches = blocks.length;
  let significant = blocks.filter((b) => b.mins >= minMins);
  if (significant.length > capCount) {
    // Keep the biggest blocks (real focus time), but present them back in
    // chronological order so the model still sees a coherent day shape.
    significant = [...significant]
      .sort((a, b) => b.mins - a.mins)
      .slice(0, capCount)
      .sort((a, b) => a.start.localeCompare(b.start));
  }
  return { blocks: significant, totalSwitches };
};

/* ─── AI summary sub-components (metric tile + titled section) ─────── */
const AiMetricTile: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode }> = ({ icon, label, value }) => (
  <div style={{ background: "#fff", border: "1px solid #efe4ff", borderRadius: 10, padding: "8px 11px" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10.5, fontWeight: 600, color: "#7c3aed" }}>
      {icon}
      {label}
    </div>
    <div style={{ fontSize: 18, fontWeight: 700, color: "var(--gt-text-primary)", marginTop: 2, fontVariantNumeric: "tabular-nums" }}>
      {value}
    </div>
  </div>
);
const AiSectionBlock: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
  <div>
    <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 5 }}>
      <span style={{ color: "#7c3aed", display: "flex" }}>{icon}</span>
      <span style={{ fontSize: 12, fontWeight: 700, color: "#6b21a8" }}>{title}</span>
    </div>
    <div style={{ fontSize: 12.5, lineHeight: 1.6, color: "var(--gt-text-primary)" }}>{children}</div>
  </div>
);

/* ─── Productivity Summary Bar (per day) ───────────────── */
const ProductivitySummaryBar: React.FC<{
  appUsage: AppUsage[];
  sites: BrowserSite[];
  timeline: TimelineEvent[];
  employeeID: string;
  employeeName: string;
  date: string;
  totalTrackedMins: number;
  activeMins: number;
  idleMins: number;
  firstActivity: string;
  lastActivity: string;
  idlePeriodsCount: number;
  idlePeriods: IdlePeriod[];
}> = ({ appUsage, sites, timeline, employeeID, employeeName, date, totalTrackedMins, activeMins, idleMins, firstActivity, lastActivity, idlePeriodsCount, idlePeriods }) => {
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [aiSections, setAiSections] = useState<AiSections | null>(null);
  const [aiTasks, setAiTasks] = useState<AiTaskSummary | null>(null);
  const [aiAppActivity, setAiAppActivity] = useState<AiAppActivity[] | null>(null);
  const [aiSwitches, setAiSwitches] = useState<number | null>(null);
  const [aiSource, setAiSource] = useState<string | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const [roleTitle, setRoleTitle] = useState<string | null>(null);
  const [roleAdjusted, setRoleAdjusted] = useState<RoleAdjustedTotals | null>(null);

  const s = summarizeSession({ appUsage, sites });
  if (s.classified <= 0) return null;

  const pct = (v: number) => (s.classified > 0 ? (v / s.classified) * 100 : 0);
  const scoreColor =
    s.score >= 70 ? CATEGORY_META.productive.fg
      : s.score >= 40 ? CATEGORY_META.neutral.fg
        : CATEGORY_META.unproductive.fg;

  const legend: Array<{ cat: Category; mins: number }> = [
    { cat: "productive", mins: s.productive },
    { cat: "neutral", mins: s.neutral },
    { cat: "unproductive", mins: s.unproductive },
  ];

  const handleAiSummary = async () => {
    if (aiLoading) return;
    setAiOpen(true);
    setAiLoading(true);
    setAiError(null);
    setAiSections(null);
    setAiTasks(null);
    setAiAppActivity(null);
    setAiSwitches(null);

    const topApps = [...appUsage]
      .sort((a, b) => b.duration_mins - a.duration_mins)
      .slice(0, 10)
      .map((a) => ({ name: a.app_name, mins: Math.round(a.duration_mins * 10) / 10, category: classifyActivity({ app_name: a.app_name }) }));
    const topSites = [...sites]
      .sort((a, b) => b.duration_mins - a.duration_mins)
      .slice(0, 10)
      .map((sSite) => ({ title: sSite.title, mins: Math.round(sSite.duration_mins * 10) / 10, category: classifyActivity({ url: sSite.url, title: sSite.title }) }));

    // Whichever apps ate the most time today get their window/tab detail sent
    // along — not hardcoded to any specific app. Browsers reuse the already-
    // extracted `sites` (has URLs); everything else reads its own window
    // titles straight from the timeline.
    const topAppsForDetail = [...appUsage].sort((a, b) => b.duration_mins - a.duration_mins).slice(0, 3);
    const appActivity = topAppsForDetail.map((a) => {
      const windows = isBrowserApp(a.app_name)
        ? sites.slice(0, 8).map((sSite) => ({ title: sSite.title, mins: Math.round(sSite.duration_mins * 10) / 10, url: sSite.url }))
        : getAppWindowTitles(timeline, a.app_name, 8).map((w) => ({ title: w.title, mins: Math.round(w.mins * 10) / 10 }));
      return {
        app_name: a.app_name,
        total_mins: Math.round(a.duration_mins * 10) / 10,
        category: classifyActivity({ app_name: a.app_name }),
        windows,
      };
    });

    const { blocks: timelineFlow, totalSwitches } = buildTimelineFlow(timeline);

    try {
      const r = await api.post<AiSummaryResponse>("/api/system-level-tracking/ai-summary", {
        employee_id: employeeID,
        employee_name: employeeName,
        date,
        total_tracked_mins: totalTrackedMins,
        active_mins: activeMins,
        idle_mins: idleMins,
        first_activity: firstActivity,
        last_activity: lastActivity,
        productivity_score: s.score,
        productive_mins: Math.round(s.productive * 10) / 10,
        neutral_mins: Math.round(s.neutral * 10) / 10,
        unproductive_mins: Math.round(s.unproductive * 10) / 10,
        top_apps: topApps,
        top_sites: topSites,
        idle_periods_count: idlePeriodsCount,
        idle_periods: idlePeriods.map((p) => ({
          start_time: p.start_time, end_time: p.end_time, duration_mins: p.duration_mins,
        })),
        app_activity: appActivity,
        timeline_flow: timelineFlow,
        context_switches: totalSwitches,
      });
      if (r.data.ok && r.data.summary) {
        setAiSummary(r.data.summary);
        setAiSections(r.data.sections || null);
        setAiTasks(r.data.tasks || null);
        setAiAppActivity(appActivity as AiAppActivity[]);
        setAiSwitches(totalSwitches);
        setAiSource(r.data.source || null);
        setRoleTitle(r.data.role_title || null);
        setRoleAdjusted(r.data.role_adjusted || null);
      } else {
        setAiError(r.data.message || "Couldn't generate a summary. Try again.");
      }
    } catch (err: any) {
      setAiError(err?.response?.data?.message || err?.message || "AI request failed. Try again.");
    } finally {
      setAiLoading(false);
    }
  };

  const scoreColorFor = (v: number) =>
    v >= 70 ? CATEGORY_META.productive.fg : v >= 40 ? CATEGORY_META.neutral.fg : CATEGORY_META.unproductive.fg;
  const catColor = (c: Category) =>
    c === "productive" ? CATEGORY_META.productive.fg
      : c === "unproductive" ? CATEGORY_META.unproductive.fg
        : "var(--gt-text-secondary)";
  // Metrics-strip values, derived from data already in scope.
  const longestBreakMins = idlePeriods.reduce((mx, p) => Math.max(mx, p.duration_mins || 0), 0);
  const taskDone = aiTasks ? aiTasks.completed.length : 0;
  const taskTotal = aiTasks
    ? new Set([...aiTasks.completed, ...aiTasks.created, ...aiTasks.due].map((t) => t.title)).size
    : 0;
  const CLOSED_STATUSES = ["DONE", "COMPLETED", "CANCELLED", "MISSED"];
  const dueOpen = aiTasks ? aiTasks.due.filter((t) => !CLOSED_STATUSES.includes(t.status)) : [];

  return (
    <div style={{ background: "var(--gt-surface-1)", border: "1px solid var(--gt-border)", borderRadius: 14, padding: "14px 16px" }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--gt-text-tertiary)" }}>
            Productivity
          </span>
          <span style={{ fontSize: 22, fontWeight: 800, color: scoreColor, fontVariantNumeric: "tabular-nums" }}>
            {s.score}%
          </span>
          <span style={{ fontSize: 10, color: "var(--gt-text-tertiary)" }}>productive of productive+unproductive</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          {legend.map(({ cat, mins }) => (
            <span key={cat} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, color: "var(--gt-text-secondary)" }}>
              <span style={{ width: 8, height: 8, borderRadius: 3, background: CATEGORY_META[cat].dot }} />
              {CATEGORY_META[cat].label} {fmtMins(mins)}
            </span>
          ))}
          <button
            onClick={handleAiSummary}
            disabled={aiLoading}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              fontSize: 11, fontWeight: 600, color: "#fff",
              background: aiLoading ? "var(--gt-text-disabled)" : "linear-gradient(135deg, #7c3aed, #a855f7)",
              border: "none", borderRadius: 20, padding: "6px 12px",
              cursor: aiLoading ? "default" : "pointer",
            }}
          >
            {aiLoading ? <Loader2 style={{ width: 12, height: 12, animation: "gt-spin 700ms linear infinite" }} /> : <Sparkles style={{ width: 12, height: 12 }} />}
            {aiLoading ? "Summarizing…" : "AI Summary"}
          </button>
        </div>
      </div>
      <div style={{ display: "flex", height: 10, borderRadius: 99, overflow: "hidden", background: "var(--gt-surface-2)" }}>
        <div style={{ width: `${pct(s.productive)}%`, background: CATEGORY_META.productive.dot }} title={`Productive ${fmtMins(s.productive)}`} />
        <div style={{ width: `${pct(s.neutral)}%`, background: CATEGORY_META.neutral.dot }} title={`Neutral ${fmtMins(s.neutral)}`} />
        <div style={{ width: `${pct(s.unproductive)}%`, background: CATEGORY_META.unproductive.dot }} title={`Unproductive ${fmtMins(s.unproductive)}`} />
      </div>

      {aiOpen && (
        <div style={{ marginTop: 12, background: "#faf5ff", border: "1px solid #e9d5ff", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 14px", background: "#f3e8ff", borderBottom: "1px solid #e9d5ff" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Sparkles style={{ width: 12, height: 12, color: "#7c3aed" }} />
              <span style={{ fontSize: 11, fontWeight: 600, color: "#6b21a8" }}>AI Summary</span>
              {aiSource && (
                <span style={{ fontSize: 9, color: "#7c3aed", background: "#e9d5ff", padding: "2px 7px", borderRadius: 20 }}>
                  {aiSource}
                </span>
              )}
            </div>
            <button
              onClick={() => setAiOpen(false)}
              style={{ background: "transparent", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#6b21a8" }}
            >
              <X style={{ width: 12, height: 12 }} /> Close
            </button>
          </div>
          <div style={{ padding: "12px 14px" }}>
            {aiLoading && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--gt-text-tertiary)" }}>
                <Loader2 style={{ width: 13, height: 13, animation: "gt-spin 700ms linear infinite" }} />
                Generating summary…
              </div>
            )}
            {!aiLoading && aiError && (
              <p style={{ fontSize: 12, color: "#dc2626" }}>{aiError}</p>
            )}
            {!aiLoading && !aiError && (aiSections || aiSummary) && (
              aiSections ? (
                <div>
                  {roleAdjusted && roleTitle && (
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "#f3e8ff", borderRadius: 8, padding: "5px 10px", marginBottom: 12, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: "#6b21a8", textTransform: "uppercase", letterSpacing: "0.03em" }}>
                        Role-adjusted · {roleTitle}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 800, color: scoreColorFor(roleAdjusted.score) }}>{roleAdjusted.score}%</span>
                      {roleAdjusted.score !== s.score && (
                        <span style={{ fontSize: 10, color: "var(--gt-text-tertiary)" }}>(was {s.score}%)</span>
                      )}
                    </div>
                  )}

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(108px, 1fr))", gap: 8, marginBottom: 14 }}>
                    <AiMetricTile icon={<Zap style={{ width: 12, height: 12 }} />} label="Active" value={fmtMins(activeMins)} />
                    <AiMetricTile icon={<Shuffle style={{ width: 12, height: 12 }} />} label="Switches" value={aiSwitches ?? "—"} />
                    <AiMetricTile icon={<Coffee style={{ width: 12, height: 12 }} />} label="Longest break" value={longestBreakMins > 0 ? fmtMins(longestBreakMins) : "—"} />
                    <AiMetricTile icon={<ListChecks style={{ width: 12, height: 12 }} />} label="Tasks done" value={taskTotal > 0 ? `${taskDone} / ${taskTotal}` : "—"} />
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 14, borderTop: "1px solid #e9d5ff", paddingTop: 14 }}>
                    {aiSections.highlights && aiSections.highlights.length > 0 && (
                      <AiSectionBlock icon={<ClipboardList style={{ width: 14, height: 14 }} />} title="Highlights">
                        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                          {aiSections.highlights.map((h, i) => (
                            <div key={i} style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
                              <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#a855f7", marginTop: 7, flexShrink: 0 }} />
                              <span style={{ flex: 1 }}>{h}</span>
                            </div>
                          ))}
                        </div>
                      </AiSectionBlock>
                    )}
                    {(aiAppActivity?.length || aiSections.specific_activity) && (
                      <AiSectionBlock icon={<Monitor style={{ width: 14, height: 14 }} />} title="What they worked on">
                        {aiAppActivity && aiAppActivity.length > 0 ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                            {aiAppActivity.map((app) => (
                              <div key={app.app_name} style={{ display: "flex", gap: 8 }}>
                                <span style={{ minWidth: 84, fontWeight: 600, color: catColor(app.category) }}>{app.app_name}</span>
                                <span style={{ color: "var(--gt-text-secondary)" }}>
                                  {app.windows.slice(0, 6).map((w) => w.title).filter(Boolean).join(", ") || "—"}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          aiSections.specific_activity
                        )}
                      </AiSectionBlock>
                    )}
                    {aiSections.breaks_and_attention && (
                      <AiSectionBlock icon={<Coffee style={{ width: 14, height: 14 }} />} title="Breaks and focus">
                        {aiSections.breaks_and_attention}
                      </AiSectionBlock>
                    )}
                    {(aiTasks?.has_any || aiSections.tasks_and_planning) && (
                      <AiSectionBlock icon={<ListChecks style={{ width: 14, height: 14 }} />} title="Tasks and planning">
                        {aiTasks && aiTasks.has_any ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            {aiTasks.completed.length > 0 && (
                              <div>
                                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5, color: CATEGORY_META.productive.fg }}>
                                  <CheckCircle2 style={{ width: 13, height: 13 }} />
                                  <span style={{ fontSize: 11.5, fontWeight: 600 }}>Completed {aiTasks.completed.length}</span>
                                </div>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                  {aiTasks.completed.map((t, i) => (
                                    <span key={i} style={{ fontSize: 11.5, background: CATEGORY_META.productive.bg, color: CATEGORY_META.productive.fg, padding: "3px 9px", borderRadius: 20 }}>{t.title}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {dueOpen.length > 0 && (
                              <div>
                                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5, color: "#b45309" }}>
                                  <Circle style={{ width: 13, height: 13 }} />
                                  <span style={{ fontSize: 11.5, fontWeight: 600 }}>Due today, still open {dueOpen.length}</span>
                                </div>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                  {dueOpen.map((t, i) => (
                                    <span key={i} style={{ fontSize: 11.5, background: "#fef3c7", color: "#92400e", padding: "3px 9px", borderRadius: 20 }}>{t.title}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {aiTasks.created.length > 0 && (
                              <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--gt-text-secondary)" }}>
                                <Plus style={{ width: 13, height: 13 }} />
                                <span style={{ fontSize: 11.5, fontWeight: 600 }}>Created today {aiTasks.created.length}</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          aiSections.tasks_and_planning
                        )}
                      </AiSectionBlock>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <p style={{ fontSize: 12.5, lineHeight: 1.6, color: "var(--gt-text-primary)", margin: 0, whiteSpace: "pre-wrap" }}>
                    {aiSummary}
                  </p>
                  {roleAdjusted && roleTitle && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #e9d5ff", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#6b21a8", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        Role-adjusted for {roleTitle}
                      </span>
                      <span style={{ fontSize: 14, fontWeight: 800, color: scoreColorFor(roleAdjusted.score) }}>
                        {roleAdjusted.score}%
                      </span>
                      {roleAdjusted.score !== s.score && (
                        <span style={{ fontSize: 10, color: "var(--gt-text-tertiary)" }}>
                          (was {s.score}% before role context)
                        </span>
                      )}
                    </div>
                  )}
                </>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Timeline Details Component ─── */
const AppTimelineDetails: React.FC<{
  appName: string;
  timeline: TimelineEvent[];
  onClose: () => void;
}> = ({ appName, timeline, onClose }) => {
  const filteredEvents = timeline.filter(
    (event) => event.app_name && event.app_name.toLowerCase() === appName.toLowerCase(),
  );

  const totalDuration = filteredEvents.reduce((sum, e) => sum + (e.duration_mins || 0), 0);
  const activeCount = filteredEvents.filter((e) => e.type === "active").length;

  if (filteredEvents.length === 0) {
    return (
      <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12, padding: 20, textAlign: "center", marginTop: 12 }}>
        <AlertCircle style={{ width: 20, height: 20, color: "#dc2626", margin: "0 auto 8px" }} />
        <p style={{ fontSize: 12, color: "#dc2626" }}>No timeline data found for {appName}</p>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 12, marginLeft: 8, marginRight: 8, background: "var(--gt-primary-container)", borderRadius: 12, border: "1px solid var(--gt-accent-soft)", overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", background: "var(--gt-primary-container)", borderBottom: "1px solid var(--gt-accent-soft)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 16 }}>{getAppIcon(appName)}</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--gt-primary-deep)" }}>{appName}</span>
          <span style={{ fontSize: 10, color: "var(--gt-primary)", background: "var(--gt-accent-soft)", padding: "2px 8px", borderRadius: 20 }}>
            {filteredEvents.length} events
          </span>
        </div>
        <button onClick={onClose} style={{ background: "transparent", border: "none", borderRadius: 20, padding: "4px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "var(--gt-primary-deep)" }}>
          <X style={{ width: 14, height: 14 }} /> Close
        </button>
      </div>

      <div style={{ display: "flex", gap: 16, padding: "10px 16px", background: "#fff", borderBottom: "1px solid var(--gt-primary-container)" }}>
        <div>
          <p style={{ fontSize: 10, color: "var(--gt-text-tertiary)" }}>Total Time</p>
          <p style={{ fontSize: 16, fontWeight: 700, color: "var(--gt-primary)" }}>{fmtMins(totalDuration)}</p>
        </div>
        <div>
          <p style={{ fontSize: 10, color: "var(--gt-text-tertiary)" }}>Active Sessions</p>
          <p style={{ fontSize: 16, fontWeight: 700, color: "var(--gt-online)" }}>{activeCount}</p>
        </div>
        <div>
          <p style={{ fontSize: 10, color: "var(--gt-text-tertiary)" }}>Avg Duration</p>
          <p style={{ fontSize: 16, fontWeight: 700, color: "var(--gt-away)" }}>
            {filteredEvents.length > 0 ? fmtMins(totalDuration / filteredEvents.length) : "0m"}
          </p>
        </div>
      </div>

      <div style={{ maxHeight: 350, overflowY: "auto", padding: "8px 16px" }}>
        {filteredEvents.map((event, idx) => (
          <div
            key={`${event.time}-${idx}`}
            style={{ display: "flex", flexDirection: "column", gap: 6, padding: "10px 0", borderBottom: idx === filteredEvents.length - 1 ? "none" : "1px solid var(--gt-primary-container)" }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <span style={{ fontSize: 10, fontFamily: "monospace", color: "var(--gt-text-tertiary)", width: 60, flexShrink: 0 }}>
                {fmtTime(event.time)}
              </span>
              <span style={{
                fontSize: 9, padding: "2px 6px", borderRadius: 10, fontWeight: 500,
                background: event.type === "idle" ? "#fff7ed" : event.type === "system_event" ? "var(--gt-border)" : "var(--gt-primary-container)",
                color: event.type === "idle" ? "var(--gt-away)" : event.type === "system_event" ? "var(--gt-text-tertiary)" : "var(--gt-online)",
                flexShrink: 0, width: 65, textAlign: "center",
              }}>
                {event.type === "idle" ? "Idle" : event.type === "system_event" ? "System" : "Active"}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                {event.title && (
                  <p style={{ fontSize: 11, color: "var(--gt-text-primary)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {truncateText(event.title, 70)}
                  </p>
                )}
                {event.label && event.label !== "SESSION_START" && event.label !== "SHUTDOWN" && (
                  <p style={{ fontSize: 10, color: "var(--gt-text-tertiary)", marginTop: 2 }}>{event.label}</p>
                )}
              </div>
              {event.duration_mins > 0 && (
                <span style={{ fontSize: 10, fontWeight: 500, color: "var(--gt-primary)", flexShrink: 0 }}>
                  {fmtMins(event.duration_mins)}
                </span>
              )}
            </div>

            {event.url && event.url !== "browser" && event.url !== "unknown" && event.url !== "null" && (
              <div style={{ marginLeft: 70 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <Globe style={{ width: 11, height: 11, color: "var(--gt-text-tertiary)" }} />
                  <span style={{ fontSize: 10, color: "var(--gt-text-tertiary)" }}>URL:</span>
                  <ClickablePath path={event.url} />
                </div>
              </div>
            )}

            {event.file_path && (
              <div style={{ marginLeft: 70 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <Folder style={{ width: 11, height: 11, color: "#f59e0b" }} />
                  <span style={{ fontSize: 10, color: "var(--gt-text-tertiary)" }}>File Path:</span>
                  <ClickablePath path={event.file_path} />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── NEW: Remote Session Details Component ──────────────── */
const RemoteSessionDetails: React.FC<{
  sessions: RemoteSession[];
  onClose: () => void;
}> = ({ sessions, onClose }) => {
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;

  const totalPages = Math.ceil(sessions.length / PAGE_SIZE);
  const pagedSessions = sessions.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Filter: only show sessions that have ocr_text or ocr_urls with content
  const sessionsWithData = sessions.filter(
    (s) => s.ocr_text || (s.ocr_urls && s.ocr_urls.length > 0) || (s.ocr_paths && s.ocr_paths.length > 0)
  );
  const displaySessions = sessionsWithData.length > 0 ? sessionsWithData : sessions;
  const pagedDisplay = displaySessions.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPagesDisplay = Math.ceil(displaySessions.length / PAGE_SIZE);

  return (
    <div
      style={{
        marginTop: 12,
        marginLeft: 8,
        marginRight: 8,
        background: "#f0f9ff",
        borderRadius: 12,
        border: "1px solid #bae6fd",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 16px",
          background: "#e0f2fe",
          borderBottom: "1px solid #bae6fd",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Monitor style={{ width: 14, height: 14, color: "#0369a1" }} />
          <span style={{ fontSize: 12, fontWeight: 600, color: "#0c4a6e" }}>Remote Sessions</span>
          <span
            style={{
              fontSize: 10,
              color: "#0369a1",
              background: "#bae6fd",
              padding: "2px 8px",
              borderRadius: 20,
            }}
          >
            {sessions.length} session{sessions.length !== 1 ? "s" : ""}
          </span>
        </div>
        <button
          onClick={onClose}
          style={{
            background: "transparent",
            border: "none",
            borderRadius: 20,
            padding: "4px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 4,
            fontSize: 11,
            color: "#0369a1",
          }}
        >
          <X style={{ width: 14, height: 14 }} /> Close
        </button>
      </div>

      {/* Summary Row */}
      <div
        style={{
          display: "flex",
          gap: 16,
          padding: "10px 16px",
          background: "#fff",
          borderBottom: "1px solid #e0f2fe",
        }}
      >
        <div>
          <p style={{ fontSize: 10, color: "var(--gt-text-tertiary)" }}>Total Sessions</p>
          <p style={{ fontSize: 16, fontWeight: 700, color: "#0369a1" }}>{sessions.length}</p>
        </div>
        <div>
          <p style={{ fontSize: 10, color: "var(--gt-text-tertiary)" }}>With OCR Data</p>
          <p style={{ fontSize: 16, fontWeight: 700, color: "var(--gt-online)" }}>{sessionsWithData.length}</p>
        </div>
        <div>
          <p style={{ fontSize: 10, color: "var(--gt-text-tertiary)" }}>Showing</p>
          <p style={{ fontSize: 16, fontWeight: 700, color: "var(--gt-primary)" }}>{displaySessions.length}</p>
        </div>
      </div>

      {/* Sessions List — paginated */}
      <div style={{ maxHeight: 500, overflowY: "auto", padding: "8px 0" }}>
        {pagedDisplay.map((session, idx) => {
          const absoluteIdx = page * PAGE_SIZE + idx;
          const isExpanded = expandedIdx === absoluteIdx;
          const hasOcr = !!session.ocr_text;
          const hasUrls = session.ocr_urls && session.ocr_urls.length > 0;
          const hasPaths = session.ocr_paths && session.ocr_paths.length > 0;

          return (
            <div key={absoluteIdx} style={{ borderBottom: idx === pagedDisplay.length - 1 ? "none" : "1px solid #e0f2fe" }}>
              {/* Session Row */}
              <div
                onClick={() => setExpandedIdx(isExpanded ? null : absoluteIdx)}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  padding: "10px 16px",
                  cursor: "pointer",
                  background: isExpanded ? "#e0f2fe" : "transparent",
                  transition: "background 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  if (!isExpanded) e.currentTarget.style.background = "#f0f9ff";
                }}
                onMouseLeave={(e) => {
                  if (!isExpanded) e.currentTarget.style.background = "transparent";
                }}
              >
                {/* Icon */}
                <div
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 8,
                    background: isExpanded ? "#bae6fd" : "#e0f2fe",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Monitor style={{ width: 14, height: 14, color: "#0369a1" }} />
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: isExpanded ? "#0c4a6e" : "var(--gt-text-primary)" }}>
                      {session.app || "Remote Desktop (RDP)"}
                    </span>
                    {hasOcr && (
                      <span style={{ fontSize: 9, background: "#fef3c7", color: "#d97706", padding: "2px 6px", borderRadius: 10, fontWeight: 500 }}>
                        OCR
                      </span>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
                    <Clock style={{ width: 10, height: 10, color: "var(--gt-text-tertiary)" }} />
                    <span style={{ fontSize: 10, fontFamily: "monospace", color: "var(--gt-text-tertiary)" }}>
                      {fmtTime(session.time)}
                    </span>
                    {session.end_time && (
                      <>
                        <span style={{ color: "var(--gt-text-disabled)", fontSize: 10 }}>→</span>
                        <span style={{ fontSize: 10, fontFamily: "monospace", color: "var(--gt-text-tertiary)" }}>
                          {fmtTime(session.end_time)}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Chevron only */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                  <ChevronRight
                    style={{
                      width: 14,
                      height: 14,
                      color: isExpanded ? "#0369a1" : "var(--gt-text-disabled)",
                      transition: "transform 0.2s ease",
                      transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                    }}
                  />
                </div>
              </div>

              {/* Expanded Detail Panel */}
              {isExpanded && (
                <div
                  style={{
                    padding: "12px 16px 16px",
                    background: "#f8fbff",
                    borderTop: "1px solid #bae6fd",
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                  }}
                >
                  {/* Time row only */}
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 12,
                      background: "#e0f2fe",
                      borderRadius: 8,
                      padding: "8px 12px",
                    }}
                  >
                    <div>
                      <p style={{ fontSize: 9, color: "var(--gt-text-tertiary)", textTransform: "uppercase", fontWeight: 600 }}>Start Time</p>
                      <p style={{ fontSize: 12, fontFamily: "monospace", fontWeight: 700, color: "#0c4a6e" }}>
                        {fmtTime(session.time)}
                      </p>
                    </div>
                    <div>
                      <p style={{ fontSize: 9, color: "var(--gt-text-tertiary)", textTransform: "uppercase", fontWeight: 600 }}>End Time</p>
                      <p style={{ fontSize: 12, fontFamily: "monospace", fontWeight: 700, color: "#0c4a6e" }}>
                        {fmtTime(session.end_time)}
                      </p>
                    </div>
                  </div>

                  {/* OCR Text */}
                  {hasOcr && (
                    <div>
                      <p
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: "var(--gt-text-tertiary)",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          marginBottom: 6,
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        <FileText style={{ width: 10, height: 10 }} />
                        OCR Text (Screen Content)
                      </p>
                      <div
                        style={{
                          background: "var(--gt-surface-2)",
                          border: "1px solid var(--gt-border-strong)",
                          borderRadius: 8,
                          padding: "12px 14px",
                          maxHeight: 200,
                          overflowY: "auto",
                        }}
                      >
                        <pre
                          style={{
                            fontSize: 12,
                            color: "var(--gt-text-secondary)",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            margin: 0,
                            fontFamily: "monospace",
                            lineHeight: 1.7,
                          }}
                        >
                          {session.ocr_text}
                        </pre>
                      </div>
                    </div>
                  )}

                  {/* OCR URLs */}
                  {hasUrls && (
                    <div>
                      <p
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: "var(--gt-text-tertiary)",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          marginBottom: 6,
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        <Globe style={{ width: 10, height: 10 }} />
                        Detected URLs ({session.ocr_urls!.length})
                      </p>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {session.ocr_urls!.map((url, i) => (
                          <div
                            key={i}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              padding: "5px 8px",
                              background: "#eff6ff",
                              borderRadius: 6,
                              border: "1px solid #bfdbfe",
                            }}
                          >
                            <Wifi style={{ width: 10, height: 10, color: "#3b82f6", flexShrink: 0 }} />
                            <ClickablePath path={url.startsWith("http") ? url : `https://${url}`}>
                              {url}
                            </ClickablePath>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* OCR Paths */}
                  {hasPaths && (
                    <div>
                      <p
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: "var(--gt-text-tertiary)",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          marginBottom: 6,
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        <Folder style={{ width: 10, height: 10 }} />
                        Detected Paths ({session.ocr_paths!.length})
                      </p>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {session.ocr_paths!.map((path, i) => (
                          <div
                            key={i}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              padding: "5px 8px",
                              background: "#fffbeb",
                              borderRadius: 6,
                              border: "1px solid #fde68a",
                            }}
                          >
                            <Folder style={{ width: 10, height: 10, color: "#f59e0b", flexShrink: 0 }} />
                            <span
                              style={{
                                fontSize: 10,
                                fontFamily: "monospace",
                                color: "#92400e",
                                wordBreak: "break-all",
                              }}
                            >
                              {path}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pagination controls */}
      {totalPagesDisplay > 1 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 16px",
            borderTop: "1px solid #e0f2fe",
            background: "#f0f9ff",
          }}
        >
          <button
            onClick={() => { setPage((p) => Math.max(0, p - 1)); setExpandedIdx(null); }}
            disabled={page === 0}
            style={{
              padding: "4px 12px", fontSize: 11, borderRadius: 8,
              border: "1px solid #bae6fd", background: page === 0 ? "var(--gt-border)" : "#fff",
              color: page === 0 ? "var(--gt-text-tertiary)" : "#0369a1", cursor: page === 0 ? "not-allowed" : "pointer",
            }}
          >
            ← Prev
          </button>
          <span style={{ fontSize: 11, color: "var(--gt-text-tertiary)" }}>
            Page {page + 1} of {totalPagesDisplay} &nbsp;·&nbsp; {displaySessions.length} sessions
          </span>
          <button
            onClick={() => { setPage((p) => Math.min(totalPagesDisplay - 1, p + 1)); setExpandedIdx(null); }}
            disabled={page === totalPagesDisplay - 1}
            style={{
              padding: "4px 12px", fontSize: 11, borderRadius: 8,
              border: "1px solid #bae6fd", background: page === totalPagesDisplay - 1 ? "var(--gt-border)" : "#fff",
              color: page === totalPagesDisplay - 1 ? "var(--gt-text-tertiary)" : "#0369a1",
              cursor: page === totalPagesDisplay - 1 ? "not-allowed" : "pointer",
            }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

/* ─── NEW: Clickable Remote Session Row (like ClickableAppRow) ─── */
const ClickableRemoteSessionRow: React.FC<{
  sessions: RemoteSession[];
  totalTrackedMins: number;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ sessions, totalTrackedMins, isExpanded, onToggle }) => {
  const [isHovered, setIsHovered] = useState(false);

  const totalDuration = sessions.reduce((sum, s) => sum + (s.duration_mins || 0), 0);
  const barWidth = totalTrackedMins > 0 ? Math.min((totalDuration / totalTrackedMins) * 100, 100) : 20;

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 12px",
          cursor: "pointer",
          borderRadius: 10,
          transition: "all 0.2s ease",
          background: isExpanded ? "#e0f2fe" : isHovered ? "#f0f9ff" : "transparent",
        }}
        onClick={onToggle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Icon */}
        <span style={{ fontSize: 16, flexShrink: 0 }}>🖥️</span>

        {/* Name */}
        <span
          style={{
            fontSize: 12,
            color: isExpanded ? "#0369a1" : "var(--gt-text-secondary)",
            width: 110,
            flexShrink: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontWeight: isExpanded ? 600 : 400,
          }}
        >
          Remote Session
        </span>

        {/* Bar */}
        <div style={{ flex: 1, height: 7, background: "var(--gt-border)", borderRadius: 99, overflow: "hidden", minWidth: 0 }}>
          <div
            style={{
              height: "100%",
              borderRadius: 99,
              background: isExpanded ? "#0369a1" : "#38bdf8",
              width: `${barWidth}%`,
            }}
          />
        </div>

        {/* Duration */}
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "var(--gt-text-primary)",
            minWidth: 55,
            textAlign: "right",
            flexShrink: 0,
            background: "var(--gt-border)",
            padding: "2px 8px",
            borderRadius: 20,
          }}
        >
          {totalDuration > 0 ? fmtMins(totalDuration) : "—"}
        </span>

        {/* Count badge */}
        <span
          style={{
            fontSize: 9,
            background: isExpanded ? "#bae6fd" : "#e0f2fe",
            padding: "2px 6px",
            borderRadius: 20,
            color: "#0369a1",
            flexShrink: 0,
          }}
        >
          {sessions.length}
        </span>

        <ChevronRight
          style={{
            width: 14,
            height: 14,
            color: isExpanded ? "#0369a1" : "var(--gt-text-disabled)",
            flexShrink: 0,
            transition: "transform 0.2s ease",
            transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
          }}
        />
      </div>

      {isExpanded && (
        <RemoteSessionDetails sessions={sessions} onClose={onToggle} />
      )}
    </div>
  );
};

/* ─── Clickable App Row Component ─── */
const ClickableAppRow: React.FC<{
  app: AppUsage;
  max: number;
  timeline: TimelineEvent[];
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ app, max, timeline, isExpanded, onToggle }) => {
  const [isHovered, setIsHovered] = useState(false);

  const appTimelineEvents = timeline.filter(
    (e) => e.app_name && e.app_name.toLowerCase() === app.app_name.toLowerCase(),
  );
  const cat = classifyActivity({ app_name: app.app_name });

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 12px",
          cursor: "pointer",
          borderRadius: 10,
          transition: "all 0.2s ease",
          background: isExpanded ? "var(--gt-primary-container)" : isHovered ? "var(--gt-surface-2)" : "transparent",
        }}
        onClick={onToggle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <span style={{ fontSize: 16, flexShrink: 0 }}>{getAppIcon(app.app_name)}</span>
        <span
          style={{
            fontSize: 12,
            color: isExpanded ? "var(--gt-primary)" : "var(--gt-text-secondary)",
            width: 110,
            flexShrink: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontWeight: isExpanded ? 600 : 400,
          }}
        >
          {app.app_name}
        </span>
        <div style={{ flex: 1, height: 7, background: "var(--gt-border)", borderRadius: 99, overflow: "hidden", minWidth: 0 }}>
          <div
            style={{
              height: "100%",
              borderRadius: 99,
              background: isExpanded ? "var(--gt-primary)" : appColor(app.app_name),
              width: `${max > 0 ? (app.duration_mins / max) * 100 : 0}%`,
            }}
          />
        </div>
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "var(--gt-text-primary)",
            minWidth: 55,
            textAlign: "right",
            flexShrink: 0,
            background: "var(--gt-border)",
            padding: "2px 8px",
            borderRadius: 20,
          }}
        >
          {fmtMins(app.duration_mins)}
        </span>
        <ProductivityBadge cat={cat} />
        {appTimelineEvents.length > 0 && (
          <span style={{ fontSize: 9, background: "var(--gt-border-strong)", padding: "2px 6px", borderRadius: 20, color: "var(--gt-text-tertiary)", flexShrink: 0 }}>
            {appTimelineEvents.length}
          </span>
        )}
        <ChevronRight
          style={{
            width: 14,
            height: 14,
            color: isExpanded ? "var(--gt-primary)" : "var(--gt-text-disabled)",
            flexShrink: 0,
            transition: "transform 0.2s ease",
            transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
          }}
        />
      </div>
      {isExpanded && (
        <AppTimelineDetails appName={app.app_name} timeline={timeline} onClose={onToggle} />
      )}
    </div>
  );
};

/* ─── Clickable Media Row Component ─── */
const ClickableMediaRow: React.FC<{
  media: MediaItem;
  max: number;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ media, max, isExpanded, onToggle }) => {
  const [isHovered, setIsHovered] = useState(false);

  const platform = media.app_name || "Unknown";
  const cat = classifyActivity({ url: media.url, app_name: media.app_name, title: media.title });
  const isYouTube =
    media.url?.toLowerCase().includes("youtube.com") ||
    media.url?.toLowerCase().includes("youtu.be") ||
    media.title?.toLowerCase().includes("youtube") ||
    platform.toLowerCase().includes("youtube");
  const isFile = isFilePath(media.url);

  const getDisplayTitle = () => {
    if (media.title && media.title !== "Media" && media.title !== "Unknown" && media.title !== media.url) return media.title;
    if (media.url && media.url !== "browser") {
      if (isFile) return getFileName(media.url);
      return getDisplayNameFromUrl(media.url);
    }
    return "Media";
  };
  const displayTitle = getDisplayTitle();

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 12px",
          cursor: "pointer",
          borderRadius: 10,
          transition: "all 0.2s ease",
          background: isExpanded ? "#fef3c7" : isHovered ? "#fffbeb" : "transparent",
        }}
        onClick={onToggle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <span style={{ fontSize: 16, flexShrink: 0 }}>{getMediaIcon(platform)}</span>
        <span
          style={{
            fontSize: 12,
            color: isExpanded ? "#d97706" : "var(--gt-text-secondary)",
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontWeight: isExpanded ? 600 : 400,
          }}
        >
          {isYouTube ? "YouTube" : truncateText(displayTitle, 50)}
        </span>
        <div style={{ width: 80, height: 6, background: "var(--gt-border)", borderRadius: 99, overflow: "hidden" }}>
          <div
            style={{
              height: "100%",
              borderRadius: 99,
              background: isExpanded ? "#d97706" : "#ef4444",
              width: `${max > 0 ? (media.duration_mins / max) * 100 : 0}%`,
            }}
          />
        </div>
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "var(--gt-text-primary)",
            minWidth: 55,
            textAlign: "right",
            flexShrink: 0,
            background: "#fef3c7",
            padding: "2px 8px",
            borderRadius: 20,
          }}
        >
          {fmtMins(media.duration_mins)}
        </span>
        <span style={{ fontSize: 9, background: "#fef3c7", padding: "2px 6px", borderRadius: 20, color: "#d97706", flexShrink: 0 }}>
          {platform}
        </span>
        <ProductivityBadge cat={cat} />
        <ChevronRight
          style={{
            width: 14,
            height: 14,
            color: isExpanded ? "#d97706" : "var(--gt-text-disabled)",
            flexShrink: 0,
            transition: "transform 0.2s ease",
            transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
          }}
        />
      </div>

      {isExpanded && (
        <div style={{ marginTop: 8, marginLeft: 8, marginRight: 8, marginBottom: 8, background: "#fffbeb", borderRadius: 12, border: "1px solid #fde68a", overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", background: "#fef3c7", borderBottom: "1px solid #fde68a" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {isFile ? getFileIcon(media.url) : <Youtube style={{ width: 14, height: 14, color: "#ef4444" }} />}
              <span style={{ fontSize: 12, fontWeight: 600, color: "#92400e" }}>
                {isFile ? "File Details" : isYouTube ? "YouTube Media Details" : "Media Details"}
              </span>
            </div>
          </div>
          <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
              <div>
                <p style={{ fontSize: 10, color: "var(--gt-text-tertiary)" }}>Start Time</p>
                <p style={{ fontSize: 12, fontWeight: 500, color: "var(--gt-text-primary)" }}>{media.fmt_start}</p>
              </div>
              <div>
                <p style={{ fontSize: 10, color: "var(--gt-text-tertiary)" }}>End Time</p>
                <p style={{ fontSize: 12, fontWeight: 500, color: "var(--gt-text-primary)" }}>{media.fmt_end}</p>
              </div>
              <div>
                <p style={{ fontSize: 10, color: "var(--gt-text-tertiary)" }}>Duration</p>
                <p style={{ fontSize: 12, fontWeight: 600, color: "#d97706" }}>{media.fmt_dur}</p>
              </div>
              <div>
                <p style={{ fontSize: 10, color: "var(--gt-text-tertiary)" }}>Platform</p>
                <p style={{ fontSize: 12, fontWeight: 500, color: "var(--gt-text-primary)" }}>{platform}</p>
              </div>
            </div>
            {media.url && media.url !== "browser" && media.url !== "unknown" && (
              <div>
                <p style={{ fontSize: 10, color: "var(--gt-text-tertiary)", marginBottom: 4 }}>{isFile ? "File Path" : "URL"}</p>
                <ClickablePath path={media.url} />
              </div>
            )}
            {media.title && media.title !== "Media" && media.title !== "Unknown" && (
              <div>
                <p style={{ fontSize: 10, color: "var(--gt-text-tertiary)" }}>Title</p>
                <p style={{ fontSize: 11, color: "var(--gt-text-secondary)", fontWeight: 500 }}>{media.title}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Section Title with Total Time Component ────────────────── */
const SectionHeader: React.FC<{
  icon: React.ReactNode;
  title: string;
  totalMins: number;
  color?: string;
}> = ({ icon, title, totalMins, color = "var(--gt-primary)" }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {icon}
      <span style={{ fontSize: 12, fontWeight: 600, color: "var(--gt-text-secondary)" }}>{title}</span>
    </div>
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: color,
        background: `color-mix(in srgb, ${color} 12%, transparent)`,
        padding: "4px 10px",
        borderRadius: 20,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <Timer style={{ width: 12, height: 12 }} />
      Total: {fmtMins(totalMins)}
    </span>
  </div>
);

/* ─── Main Component ────────────────────────────────────── */
export const ActivityDashboard: React.FC = () => {
  // Viewing OTHER people's activity = the "activity-reports" panel at Manage in
  // Access Management; at View a user only sees their own. No role list.
  const { canManage } = useAccess();
  const canSeeOthers = canManage("activity-reports");
  const [selEmp, setSelEmp] = useState<Employee | null>(null);
  const [startDate, setStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [filteredData, setFilteredData] = useState<ActivityData[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [empSearch, setEmpSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingEmps, setLoadingEmps] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<"timeline" | "summary">("timeline");
  const [openDrop, setOpenDrop] = useState(false);
  const [apiResp, setApiResp] = useState<ApiResponse | null>(null);
  const [expandedApps, setExpandedApps] = useState<Set<string>>(new Set());
  const [expandedMedia, setExpandedMedia] = useState<Set<string>>(new Set());
  // NEW: track expanded remote sessions per session card
  const [expandedRemote, setExpandedRemote] = useState<Set<string>>(new Set());
  // Self-service mode: a plain employee / intern only ever sees their OWN
  // activity. When true, the employee picker is locked to the signed-in user
  // and the data auto-loads.
  const [selfMode, setSelfMode] = useState(false);
  // The caller's own employee row. Managerial roles (incl. supervisor) keep
  // the team picker but we inject this so they can also view their OWN report
  // — /employeesAll omits the caller, so it isn't in `employees`.
  const [selfEmp, setSelfEmp] = useState<Employee | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    getEmployees()
      .then((r) => setEmployees(r?.employees || []))
      .catch(console.error);
    (async () => {
      setLoadingEmps(true);
      try {
        const r = await api.get("/api/system-level-tracking/employees", {
          params: { limit: 100, offset: 0 },
        });
        const res: ApiResponse = r.data;
        if (res.success && res.data) {
          // Process if needed
        }
      } catch {
        /* silent */
      } finally {
        setLoadingEmps(false);
      }
    })();
  }, []);

  // Resolve the signed-in user. Managerial roles — including SUPERVISOR — keep
  // the full team picker (they monitor the employees under them) AND can view
  // their own report (we inject `selfEmp` into the list). A plain employee /
  // intern is locked to their own activity. We read the caller's own row from
  // /my_employee_profile because /employeesAll is role-scoped and omits the
  // caller themselves.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const me = await getMyEmployeeProfile();
        if (!active || !me) return;
        const meEmp: Employee | null = me.employeeID
          ? {
              employeeID: me.employeeID,
              employee_name: me.name || `${me.first_name || ""} ${me.last_name || ""}`.trim(),
              first_name: me.first_name,
              last_name: me.last_name,
              user_id: me.user_id,
            }
          : null;
        if (meEmp) setSelfEmp(meEmp);
        if (!canSeeOthers && meEmp) {
          // No manage rights on Activity Reports → lock the picker to self.
          setSelEmp(meEmp);
          setSelfMode(true);
        }
      } catch {
        /* fall back to the standard picker */
      }
    })();
    return () => { active = false; };
  }, []);

  const fetchData = async () => {
    if (!selEmp) {
      toast({ title: "Error", description: "Please select an employee", variant: "destructive" });
      return;
    }
    if (startDate > endDate) {
      toast({ title: "Error", description: "Start date cannot be after end date", variant: "destructive" });
      return;
    }
    setLoading(true);
    setError(null);
    setExpandedApps(new Set());
    setExpandedMedia(new Set());
    setExpandedRemote(new Set());

    try {
      const r = await api.get("/api/system-level-tracking/employees", {
        params: { employee_id: selEmp.employeeID, start_date: startDate, end_date: endDate, limit: 50, offset: 0 },
      });
      const res: ApiResponse = r.data;
      setApiResp(res);
      if (res.success && res.data?.length > 0) {
        // Normalize remote_sessions — extract from all possible locations
        const normalized = res.data.map((session: any) => {
          let rs =
            session.remote_sessions ||
            session.remoteSessions ||
            session.remote_session ||
            null;

          // If not found directly, try to extract from full_summary array
          if (!rs && Array.isArray(session.full_summary)) {
            // full_summary may be [{remote_sessions: [...]}, ...] or contain RDP items directly
            const fsWithRs = session.full_summary.find((s: any) => Array.isArray(s.remote_sessions));
            if (fsWithRs) {
              rs = fsWithRs.remote_sessions;
            } else {
              // full_summary items themselves may be remote session objects
              const rdpItems = session.full_summary.filter(
                (s: any) => s.app && (
                  s.app.toLowerCase().includes("remote") ||
                  s.app.toLowerCase().includes("rdp") ||
                  s.app.toLowerCase().includes("mstsc")
                )
              );
              if (rdpItems.length > 0) rs = rdpItems;
            }
          }

          return { ...session, remote_sessions: rs || [] };
        });

        setFilteredData(normalized);
        toast({ title: "Success", description: `Found ${res.data.length} record(s) for ${selEmp.first_name}` });
      } else {
        setFilteredData([]);
        setError("No data found for the selected period");
        toast({ title: "No Data", description: "No activity records found", variant: "destructive" });
      }
    } catch {
      setError("Failed to fetch activity data.");
      toast({ title: "Error", description: "Failed to fetch data", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  // In self-service mode, auto-load the signed-in user's activity once we've
  // resolved who they are — no manual "Load" click needed. Changing the date
  // range still uses the Load button (kept visible below).
  useEffect(() => {
    if (selfMode && selEmp) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selfMode, selEmp?.employeeID]);

  const toggleApp = (sessionId: string, appName: string) => {
    const key = `${sessionId}-${appName}`;
    const newExpanded = new Set(expandedApps);
    if (newExpanded.has(key)) newExpanded.delete(key);
    else newExpanded.add(key);
    setExpandedApps(newExpanded);
  };

  const toggleMedia = (sessionId: string, mediaId: string) => {
    const key = `${sessionId}-${mediaId}`;
    const newExpanded = new Set(expandedMedia);
    if (newExpanded.has(key)) newExpanded.delete(key);
    else newExpanded.add(key);
    setExpandedMedia(newExpanded);
  };

  // NEW: toggle remote sessions expansion
  const toggleRemote = (sessionId: string) => {
    const newExpanded = new Set(expandedRemote);
    if (newExpanded.has(sessionId)) newExpanded.delete(sessionId);
    else newExpanded.add(sessionId);
    setExpandedRemote(newExpanded);
  };

  const isAppExpanded = (sessionId: string, appName: string) => expandedApps.has(`${sessionId}-${appName}`);
  const isMediaExpanded = (sessionId: string, mediaId: string) => expandedMedia.has(`${sessionId}-${mediaId}`);
  const isRemoteExpanded = (sessionId: string) => expandedRemote.has(sessionId); // NEW

  // Team list for the picker, with the caller's OWN row prepended (labelled
  // "You") when it isn't already in the role-scoped /employeesAll result — so
  // a manager or supervisor can view their own report too, not just the team.
  const pickerEmployees = useMemo(() => {
    if (!selfEmp) return employees;
    // The picker row renders `first_name last_name`, so tag the last name with
    // "(You)" so the caller can spot their own row. Supervisors already appear
    // in their own /employeesAll result, so tag that existing row; for roles
    // where the caller is omitted, prepend an injected row instead.
    const tag = (e: Employee): Employee => ({ ...e, last_name: `${e.last_name || ""} (You)`.trim() });
    if (employees.some((e) => e.employeeID === selfEmp.employeeID)) {
      return employees.map((e) => (e.employeeID === selfEmp.employeeID ? tag(e) : e));
    }
    return [tag(selfEmp), ...employees];
  }, [employees, selfEmp]);

  const filteredEmps = useMemo(() => {
    if (!empSearch) return pickerEmployees;
    const s = empSearch.toLowerCase();
    return pickerEmployees.filter(
      (e) =>
        e.first_name?.toLowerCase().includes(s) ||
        e.last_name?.toLowerCase().includes(s) ||
        e.employeeID.toLowerCase().includes(s),
    );
  }, [pickerEmployees, empSearch]);

  const getBrowserSites = (session: ActivityData): BrowserSite[] => {
    const map = new Map<string, BrowserSite>();
    session.timeline.forEach((ev) => {
      if ((ev.app_name === "Chrome" || ev.app_name === "Brave") && ev.title) {
        let t = ev.title;
        if (ev.app_name === "Chrome") t = t.replace(" - Google Chrome", "");
        if (ev.app_name === "Brave") t = t.replace(" - Brave", "");
        if (!map.has(t))
          map.set(t, { key: t, url: ev.url || "", duration_mins: ev.duration_mins || 0, title: t, visits: 1 });
        else {
          const ex = map.get(t)!;
          ex.duration_mins += ev.duration_mins || 0;
          ex.visits++;
        }
      }
    });
    return Array.from(map.values()).sort((a, b) => b.duration_mins - a.duration_mins);
  };

  const getMedia = (session: ActivityData): MediaItem[] => {
    const items: MediaItem[] = session.media_detected
      .filter((m) => m.media_title || m.url)
      .map((m, i) => ({
        id: `${session.id}-${i}`,
        title: m.media_title || m.url?.split("/").pop() || "Media",
        url: m.url || "",
        app_name: m.app_name || "Unknown",
        start_time: m.start_time || "",
        end_time: m.end_time || "",
        duration_mins: m.duration_mins || 0,
        fmt_start: m.start_time ? fmtTime(m.start_time) : "N/A",
        fmt_end: m.end_time ? fmtTime(m.end_time) : "N/A",
        fmt_dur: fmtDur(m.duration_mins || 0),
      }));
    const uMap = new Map<string, MediaItem>();
    items.forEach((it) => {
      const k = it.start_time || it.title;
      if (!uMap.has(k) || uMap.get(k)!.duration_mins < it.duration_mins) uMap.set(k, it);
    });
    return Array.from(uMap.values()).sort((a, b) => b.duration_mins - a.duration_mins);
  };

  const empSummaries = (): EmployeeSummary[] => {
    const map = new Map<string, EmployeeSummary>();
    filteredData.forEach((it) => {
      if (!map.has(it.employeeID))
        map.set(it.employeeID, { employeeID: it.employeeID, employee_name: it.employee_name, total_tracked_mins: 0, active_mins: 0, idle_mins: 0, data: [] });
      const s = map.get(it.employeeID)!;
      s.total_tracked_mins += it.total_tracked_mins;
      s.active_mins += it.active_mins;
      s.idle_mins += it.idle_mins;
      s.data.push(it);
    });
    return Array.from(map.values());
  };

  const totalTracked = filteredData.reduce((s, i) => s + i.total_tracked_mins, 0);
  const totalActive = filteredData.reduce((s, i) => s + i.active_mins, 0);
  const totalIdle = filteredData.reduce((s, i) => s + i.idle_mins, 0);
  const empName = selEmp
    ? `${selEmp.first_name || ""} ${selEmp.last_name || ""}`.trim() || selEmp.employee_name
    : "";

  const card = { background: "var(--gt-surface-0)", borderRadius: 18, border: "1px solid var(--gt-border)", overflow: "hidden" as const, boxShadow: "var(--gt-elev-1)" };

  return (
    <div style={{ width: "100%", minHeight: "100vh", background: "var(--gt-surface-1)", overflowX: "hidden", boxSizing: "border-box" }}>
      {/* HEADER */}
      <div style={{ width: "100%", background: "var(--gt-surface-0)", borderBottom: "1px solid var(--gt-border)", position: "sticky", top: 0, zIndex: 20 }}>
        <div style={{ maxWidth: 1152, margin: "0 auto", padding: "14px 20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
            <div style={{ width: 38, height: 38, borderRadius: 12, background: "var(--gt-primary)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "var(--gt-glow-soft)", flexShrink: 0 }}>
              <Activity style={{ width: 18, height: 18, color: "var(--gt-on-primary)" }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h1
                style={{
                  margin: 0,
                  fontFamily: "var(--gt-font-display)",
                  fontSize: 23,
                  fontWeight: 800,
                  letterSpacing: "var(--gt-letter-display)",
                  color: "var(--gt-text-primary)",
                  lineHeight: 1.05,
                }}
              >
                Activity{" "}
                <span style={{ fontFamily: "var(--gt-font-serif)", fontStyle: "italic", fontWeight: 500 }}>Report</span>
              </h1>
              <p style={{ margin: "3px 0 0", fontSize: 12, color: "var(--gt-text-tertiary)", fontWeight: 500 }}>
                Per-employee app &amp; website activity captured by the desktop agent
              </p>
            </div>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
            <div style={{ display: "flex", background: "var(--gt-surface-2)", borderRadius: 10, padding: 3, gap: 2, border: "1px solid var(--gt-border)" }}>
              {(["timeline", "summary"] as const).map((v) => (
                <button
                  key={v}
                  onClick={() => { setView(v); setExpandedApps(new Set()); setExpandedMedia(new Set()); setExpandedRemote(new Set()); }}
                  style={{
                    padding: "5px 12px", fontSize: 11, fontWeight: 600, borderRadius: 8, border: "none", cursor: "pointer",
                    fontFamily: "var(--gt-font)",
                    background: view === v ? "var(--gt-surface-0)" : "transparent",
                    color: view === v ? "var(--gt-text-primary)" : "var(--gt-text-tertiary)",
                    boxShadow: view === v ? "var(--gt-elev-1)" : "none",
                    letterSpacing: "0.02em",
                    textTransform: "lowercase",
                  }}
                >
                  {v}
                </button>
              ))}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 6, background: "var(--gt-surface-0)", border: "1px solid var(--gt-border)", borderRadius: 10, padding: "6px 10px" }}>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ fontSize: 11, background: "transparent", border: "none", outline: "none", width: 110, color: "var(--gt-text-primary)", fontFamily: "var(--gt-font)" }}
              />
              <span style={{ color: "var(--gt-text-tertiary)", fontSize: 11 }}>→</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{ fontSize: 11, background: "transparent", border: "none", outline: "none", width: 110, color: "var(--gt-text-primary)", fontFamily: "var(--gt-font)" }}
              />
            </div>

            {selfMode ? (
              // Self-service: the user can only see their own activity, so the
              // picker is a read-only chip showing their name.
              <div
                style={{
                  display: "flex", alignItems: "center", gap: 6, height: 36, padding: "0 14px",
                  background: "var(--gt-surface-0)", border: "1px solid var(--gt-border)", borderRadius: 10,
                  fontSize: 11, minWidth: 140, maxWidth: 200,
                  color: "var(--gt-text-primary)", fontFamily: "var(--gt-font)", fontWeight: 500,
                }}
                title="You can only view your own activity"
              >
                <User style={{ width: 13, height: 13, color: "var(--gt-text-tertiary)" }} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {empName || "You"}
                </span>
              </div>
            ) : (
            <Popover open={openDrop} onOpenChange={setOpenDrop}>
              <PopoverTrigger asChild>
                <button
                  style={{
                    display: "flex", alignItems: "center", gap: 6, height: 36, padding: "0 14px",
                    background: "var(--gt-surface-0)", border: "1px solid var(--gt-border)", borderRadius: 10,
                    fontSize: 11, cursor: "pointer", minWidth: 140, maxWidth: 200,
                    color: "var(--gt-text-primary)", fontFamily: "var(--gt-font)", fontWeight: 500,
                  }}
                >
                  <User style={{ width: 13, height: 13, color: "var(--gt-text-tertiary)" }} />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {empName || "Select employee"}
                  </span>
                  <ChevronDown style={{ width: 13, height: 13, color: "var(--gt-text-tertiary)" }} />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search…" value={empSearch} onValueChange={setEmpSearch} className="h-8 text-xs" />
                  <CommandList>
                    <CommandEmpty className="text-xs py-2">
                      {loadingEmps ? "Loading…" : "No employee found."}
                    </CommandEmpty>
                    <CommandGroup>
                      {filteredEmps.map((e) => (
                        <CommandItem
                          key={e.employeeID}
                          value={`${e.first_name || ""} ${e.last_name || ""} ${e.employeeID}`}
                          onSelect={() => { setSelEmp(e); setEmpSearch(""); setOpenDrop(false); }}
                          className="cursor-pointer text-xs"
                        >
                          <Check className={`mr-2 h-3 w-3 ${selEmp?.employeeID === e.employeeID ? "opacity-100" : "opacity-0"}`} />
                          <div>
                            <p className="font-medium">{e.first_name} {e.last_name}</p>
                            <p className="text-[10px] text-slate-500">{e.employeeID}</p>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            )}

            <button
              onClick={fetchData}
              disabled={loading || !selEmp}
              style={{
                display: "flex", alignItems: "center", gap: 6, height: 36, padding: "0 18px",
                background: "var(--gt-primary)", color: "var(--gt-on-primary)",
                fontSize: 11, fontWeight: 600, fontFamily: "var(--gt-font)", letterSpacing: "0.02em",
                borderRadius: 999, border: "1px solid var(--gt-primary)",
                cursor: loading || !selEmp ? "not-allowed" : "pointer",
                opacity: loading || !selEmp ? 0.5 : 1,
                boxShadow: loading || !selEmp ? "none" : "var(--gt-glow-soft)",
              }}
            >
              {loading ? <Loader2 style={{ width: 13, height: 13 }} className="animate-spin" /> : <Search style={{ width: 13, height: 13 }} />}
              Load
            </button>
          </div>
        </div>
      </div>

      {/* BODY */}
      <div style={{ maxWidth: 1152, margin: "0 auto", padding: "20px 16px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {error && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 12, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12, color: "#dc2626", fontSize: 12 }}>
              <AlertCircle style={{ width: 14, height: 14 }} />
              <span>{error}</span>
            </div>
          )}

          {apiResp?.monthly_summary && (
            <div style={card}>
              <div style={{ padding: 20 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <BarChart3 style={{ width: 14, height: 14, color: "var(--gt-primary)" }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: "var(--gt-text-secondary)" }}>Monthly Summary</span>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 12 }}>
                  {[
                    { label: "Total Tracked", val: fmtMins(apiResp.monthly_summary.total_tracked_mins), sub: `${apiResp.monthly_summary.total_tracked_hrs.toFixed(2)} hrs`, c: "var(--gt-primary)" },
                    { label: "Active Time", val: fmtMins(apiResp.monthly_summary.total_active_mins), sub: `${apiResp.monthly_summary.total_active_hrs.toFixed(2)} hrs`, c: "var(--gt-online)" },
                    { label: "Idle Time", val: fmtMins(apiResp.monthly_summary.total_idle_mins), sub: `${apiResp.monthly_summary.total_idle_hrs.toFixed(2)} hrs`, c: "var(--gt-away)" },
                    {
                      label: "Efficiency", c: "var(--gt-primary)", sub: "of tracked time",
                      val: `${apiResp.monthly_summary.total_tracked_mins > 0 ? ((apiResp.monthly_summary.total_active_mins / apiResp.monthly_summary.total_tracked_mins) * 100).toFixed(0) : 0}%`,
                    },
                  ].map((s) => (
                    <div key={s.label} style={{ background: "var(--gt-surface-2)", borderRadius: 12, padding: 16, textAlign: "center" }}>
                      <p style={{ fontSize: 10, color: "var(--gt-text-tertiary)", textTransform: "uppercase", marginBottom: 4 }}>{s.label}</p>
                      <p style={{ fontSize: 22, fontWeight: 700, color: s.c, lineHeight: 1 }}>{s.val}</p>
                      <p style={{ fontSize: 10, color: "var(--gt-text-tertiary)", marginTop: 4 }}>{s.sub}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {!selEmp && !loading && !selfMode && (
            <div style={{ ...card, padding: "44px 32px", textAlign: "center" }}>
              <div style={{ width: 56, height: 56, borderRadius: 18, background: "var(--gt-primary-container)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                <Users style={{ width: 24, height: 24, color: "var(--gt-primary)" }} />
              </div>
              <p style={{ margin: 0, fontSize: 17, fontWeight: 700, fontFamily: "var(--gt-font-display)", letterSpacing: "var(--gt-letter-display)", color: "var(--gt-text-primary)" }}>
                Pick someone to{" "}
                <span style={{ fontFamily: "var(--gt-font-serif)", fontStyle: "italic", fontWeight: 500 }}>begin</span>
              </p>
              <p style={{ margin: "6px auto 0", fontSize: 12.5, color: "var(--gt-text-tertiary)", maxWidth: 340, lineHeight: 1.5 }}>
                Choose an employee and a date range above, then load to see their app, website and idle activity.
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 8, marginTop: 20 }}>
                {[
                  { n: "1", Icon: User, t: "Select employee" },
                  { n: "2", Icon: Calendar, t: "Set date range" },
                  { n: "3", Icon: Search, t: "Load activity" },
                ].map((step) => (
                  <div key={step.n} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "7px 13px", background: "var(--gt-surface-1)", border: "1px solid var(--gt-border)", borderRadius: 99 }}>
                    <span style={{ width: 18, height: 18, borderRadius: 99, background: "var(--gt-primary)", color: "var(--gt-on-primary)", fontSize: 10, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{step.n}</span>
                    <step.Icon style={{ width: 13, height: 13, color: "var(--gt-text-tertiary)" }} />
                    <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--gt-text-secondary)" }}>{step.t}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selEmp && !loading && filteredData.length === 0 && !error && (
            <div style={{ ...card, padding: "44px 32px", textAlign: "center" }}>
              <div style={{ width: 56, height: 56, borderRadius: 18, background: "var(--gt-surface-2)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                <Search style={{ width: 24, height: 24, color: "var(--gt-text-tertiary)" }} />
              </div>
              <p style={{ margin: 0, fontSize: 16, fontWeight: 700, fontFamily: "var(--gt-font-display)", color: "var(--gt-text-primary)" }}>No activity found</p>
              <p style={{ margin: "6px auto 0", fontSize: 12.5, color: "var(--gt-text-tertiary)", maxWidth: 320, lineHeight: 1.5 }}>
                No records for {empName || "this employee"} in the selected period. Try a wider date range.
              </p>
            </div>
          )}

          {loading && (
            <div style={{ display: "flex", justifyContent: "center", padding: 64 }}>
              <Loader2 className="animate-spin" style={{ width: 24, height: 24, color: "var(--gt-primary)" }} />
            </div>
          )}

          {filteredData.length > 0 && (
            <>
              {(() => {
                const activePct = totalTracked > 0 ? (totalActive / totalTracked) * 100 : 0;
                const idlePct = totalTracked > 0 ? (totalIdle / totalTracked) * 100 : 0;
                const metrics = [
                  { label: "Records", val: String(filteredData.length), Icon: CalendarDays, c: "var(--gt-primary)", tint: "var(--gt-primary-container)" },
                  { label: "Total Tracked", val: fmtMins(totalTracked), Icon: Clock, c: "var(--gt-primary)", tint: "var(--gt-primary-container)" },
                  { label: "Active", val: fmtMins(totalActive), Icon: Zap, c: "var(--gt-online)", tint: "color-mix(in srgb, var(--gt-online) 15%, transparent)" },
                  { label: "Idle", val: fmtMins(totalIdle), Icon: Hourglass, c: "var(--gt-away)", tint: "color-mix(in srgb, var(--gt-away) 18%, transparent)" },
                  { label: "Efficiency", val: `${activePct.toFixed(0)}%`, Icon: Gauge, c: "var(--gt-primary)", tint: "var(--gt-primary-container)" },
                ];
                return (
                  <div style={{ ...card, padding: 18 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
                      {metrics.map((m) => (
                        <div key={m.label} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: "var(--gt-surface-1)", border: "1px solid var(--gt-border)", borderRadius: 14 }}>
                          <div style={{ width: 38, height: 38, borderRadius: 11, background: m.tint, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <m.Icon style={{ width: 18, height: 18, color: m.c }} />
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <p style={{ margin: 0, fontSize: 9, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--gt-text-tertiary)" }}>{m.label}</p>
                            <p style={{ margin: "2px 0 0", fontSize: 19, fontWeight: 700, lineHeight: 1.05, color: m.c, fontVariantNumeric: "tabular-nums" }}>{m.val}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    {totalTracked > 0 && (
                      <div style={{ marginTop: 16 }}>
                        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 7 }}>
                          <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--gt-text-tertiary)" }}>Focus balance</span>
                          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, color: "var(--gt-text-secondary)" }}>
                              <span style={{ width: 8, height: 8, borderRadius: 3, background: "var(--gt-online)" }} /> Active {activePct.toFixed(0)}%
                            </span>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, color: "var(--gt-text-secondary)" }}>
                              <span style={{ width: 8, height: 8, borderRadius: 3, background: "var(--gt-away)" }} /> Idle {idlePct.toFixed(0)}%
                            </span>
                          </div>
                        </div>
                        <div style={{ display: "flex", height: 9, borderRadius: 99, overflow: "hidden", background: "var(--gt-surface-2)" }}>
                          <div style={{ width: `${Math.min(activePct, 100)}%`, background: "var(--gt-online)" }} />
                          <div style={{ width: `${Math.min(idlePct, 100 - Math.min(activePct, 100))}%`, background: "var(--gt-away)" }} />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {view === "timeline" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {filteredData.map((session) => {
                    const sites = getBrowserSites(session);
                    const media = getMedia(session);
                    const remoteSessions = session.remote_sessions || [];
                    const visibleAppsRaw = session.app_usage.filter(
                      (a) => !HIDDEN_APPS.map(h => h.toLowerCase()).includes(a.app_name.toLowerCase())
                    );
                    // Insert Remote Desktop (RDP) as synthetic app entry sorted by duration
                    const rdpDuration = remoteSessions.reduce((sum: number, s: RemoteSession) => sum + (s.duration_mins || 0), 0);
                    const rdpEntry: AppUsage & { _isRemote: true } = { app_name: "Remote Desktop (RDP)", duration_mins: rdpDuration, _isRemote: true } as any;
                    const visibleApps = remoteSessions.length > 0
                      ? [...visibleAppsRaw, rdpEntry].sort((a, b) => b.duration_mins - a.duration_mins)
                      : visibleAppsRaw;
                    const maxApp = Math.max(...visibleApps.map((a) => a.duration_mins), 1);
                    const maxSite = Math.max(...sites.map((s) => s.duration_mins), 1);
                    const maxMedia = Math.max(...media.map((m) => m.duration_mins), 1);

                    const totalAppMins = visibleApps.reduce((sum, a) => sum + a.duration_mins, 0);
                    const totalSiteMins = sites.reduce((sum, s) => sum + s.duration_mins, 0);
                    const totalMediaMins = media.reduce((sum, m) => sum + m.duration_mins, 0);

                    return (
                      <div key={session.id} style={card}>
                        {/* Session Header */}
                        <div
                          style={{
                            display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12,
                            padding: "16px 20px", borderBottom: "1px solid var(--gt-border)",
                          }}
                        >
                          <div style={{ width: 36, height: 36, borderRadius: 12, background: "var(--gt-primary-container)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <User style={{ width: 16, height: 16, color: "var(--gt-primary)" }} />
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <p style={{ fontSize: 13, fontWeight: 600, color: "var(--gt-text-primary)" }}>{session.employee_name}</p>
                            <p style={{ fontSize: 11, color: "var(--gt-text-tertiary)" }}>{fmtDate(session.date)}</p>
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginLeft: "auto" }}>
                            <Chip label="Tracked" value={fmtMins(session.total_tracked_mins)} />
                            <Chip label="Active" value={fmtMins(session.active_mins)} color="var(--gt-online)" />
                            <Chip label="Idle" value={fmtMins(session.idle_mins)} color="var(--gt-away)" />
                            <Chip label="Login" value={fmtTime(session.first_activity)} />
                            <Chip label="Logout" value={fmtTime(session.last_activity)} />
                          </div>
                        </div>

                        <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 24 }}>
                          {/* Productivity Summary — productive / neutral / unproductive split */}
                          <ProductivitySummaryBar
                            appUsage={visibleAppsRaw}
                            sites={sites}
                            timeline={session.timeline}
                            employeeID={session.employeeID}
                            employeeName={session.employee_name}
                            date={session.date}
                            totalTrackedMins={session.total_tracked_mins}
                            activeMins={session.active_mins}
                            idleMins={session.idle_mins}
                            firstActivity={session.first_activity}
                            lastActivity={session.last_activity}
                            idlePeriodsCount={session.idle_periods?.length || 0}
                            idlePeriods={session.idle_periods || []}
                          />

                          {/* Applications Section — now includes Remote Sessions row */}
                          {(visibleApps.length > 0 || remoteSessions.length > 0) && (
                            <div>
                              <SectionHeader
                                icon={<AppWindow style={{ width: 13, height: 13, color: "var(--gt-text-tertiary)" }} />}
                                title="Applications (Click to see timeline)"
                                totalMins={totalAppMins}
                                color="var(--gt-primary)"
                              />
                              <div
                                style={{
                                  overflowY: "visible",
                                  paddingRight: 4,
                                  scrollbarWidth: "thin",
                                  border: "1px solid var(--gt-border)",
                                  borderRadius: 12,
                                  background: "var(--gt-surface-1)",
                                }}
                              >
                                {/* App rows — remote session inserted at sorted position */}
                                {visibleApps.map((a: any, i) =>
                                  a._isRemote ? (
                                    <ClickableRemoteSessionRow
                                      key="remote-session"
                                      sessions={remoteSessions}
                                      totalTrackedMins={session.total_tracked_mins}
                                      isExpanded={isRemoteExpanded(session.id)}
                                      onToggle={() => toggleRemote(session.id)}
                                    />
                                  ) : (
                                    <ClickableAppRow
                                      key={i}
                                      app={a}
                                      max={maxApp}
                                      timeline={session.timeline}
                                      isExpanded={isAppExpanded(session.id, a.app_name)}
                                      onToggle={() => toggleApp(session.id, a.app_name)}
                                    />
                                  )
                                )}
                              </div>
                            </div>
                          )}

                          {/* Sites Section */}
                          {sites.length > 0 && (
                            <div>
                              <SectionHeader
                                icon={<Globe style={{ width: 13, height: 13, color: "var(--gt-text-tertiary)" }} />}
                                title="Sites Visited"
                                totalMins={totalSiteMins}
                                color="#3b82f6"
                              />
                              <div
                                style={{
                                  maxHeight: 250, overflowY: "auto", paddingRight: 4, scrollbarWidth: "thin",
                                  border: "1px solid var(--gt-border)", borderRadius: 12, padding: "8px 12px",
                                }}
                              >
                                {sites.map((s, i) => (
                                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0" }}>
                                    <span style={{ fontSize: 11, color: "var(--gt-text-secondary)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {truncateText(s.title, 40)}
                                    </span>
                                    <div style={{ width: 80, height: 6, background: "var(--gt-border)", borderRadius: 99, overflow: "hidden" }}>
                                      <div style={{ height: "100%", borderRadius: 99, background: "#60a5fa", width: `${(s.duration_mins / maxSite) * 100}%` }} />
                                    </div>
                                    <span style={{ fontSize: 10, fontWeight: 500, color: "#3b82f6", minWidth: 52, textAlign: "right" }}>
                                      {fmtMins(s.duration_mins)}
                                    </span>
                                    <ProductivityBadge cat={classifyActivity({ url: s.url, title: s.title })} />
                                    <span style={{ fontSize: 9, color: "var(--gt-text-tertiary)", width: 28, textAlign: "right" }}>{s.visits}v</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Media Section */}
                          {media.length > 0 && (
                            <div>
                              <SectionHeader
                                icon={<Youtube style={{ width: 13, height: 13, color: "#ef4444" }} />}
                                title="Media Detected (Click to see details)"
                                totalMins={totalMediaMins}
                                color="#ef4444"
                              />
                              <div
                                style={{
                                  maxHeight: 400, overflowY: "auto", paddingRight: 4, scrollbarWidth: "thin",
                                  border: "1px solid var(--gt-border)", borderRadius: 12, background: "var(--gt-surface-1)",
                                }}
                              >
                                {media.map((m) => (
                                  <ClickableMediaRow
                                    key={m.id}
                                    media={m}
                                    max={maxMedia}
                                    isExpanded={isMediaExpanded(session.id, m.id)}
                                    onToggle={() => toggleMedia(session.id, m.id)}
                                  />
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Idle Periods */}
                          {session.idle_periods.length > 0 && (
                            <div>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                                <Hourglass style={{ width: 13, height: 13, color: "var(--gt-text-tertiary)" }} />
                                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--gt-text-secondary)" }}>Idle Periods</span>
                              </div>
                              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                {session.idle_periods.map((p, i) => (
                                  <div
                                    key={i}
                                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: "var(--gt-surface-2)", borderRadius: 10 }}
                                  >
                                    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--gt-text-secondary)" }}>
                                      <Clock style={{ width: 12, height: 12, color: "var(--gt-text-tertiary)" }} />
                                      <span style={{ fontFamily: "monospace" }}>{fmtTime(p.start_time)}</span>
                                      <span style={{ color: "var(--gt-text-disabled)" }}>→</span>
                                      <span style={{ fontFamily: "monospace" }}>{fmtTime(p.end_time)}</span>
                                    </div>
                                    <span style={{ fontSize: 12, fontWeight: 600, color: "var(--gt-away)" }}>{fmtMins(p.duration_mins)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {view === "summary" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {empSummaries().map((emp) => {
                    const eff = emp.total_tracked_mins > 0 ? (emp.active_mins / emp.total_tracked_mins) * 100 : 0;
                    return (
                      <div key={emp.employeeID} style={card}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 20px", borderBottom: "1px solid var(--gt-border)" }}>
                          <div style={{ width: 36, height: 36, borderRadius: 12, background: "var(--gt-primary-container)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Users style={{ width: 16, height: 16, color: "var(--gt-primary)" }} />
                          </div>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <p style={{ fontSize: 13, fontWeight: 600, color: "var(--gt-text-primary)" }}>{emp.employee_name}</p>
                            <p style={{ fontSize: 11, color: "var(--gt-text-tertiary)" }}>{emp.employeeID}</p>
                          </div>
                          <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 99, background: "var(--gt-primary-container)", color: "var(--gt-online)", fontWeight: 500 }}>
                            {emp.data.length} session{emp.data.length !== 1 ? "s" : ""}
                          </span>
                        </div>
                        <div style={{ padding: 20 }}>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))", gap: 12, marginBottom: 20 }}>
                            {[
                              { label: "Tracked", val: fmtMins(emp.total_tracked_mins), c: "var(--gt-text-primary)" },
                              { label: "Active", val: fmtMins(emp.active_mins), c: "var(--gt-online)" },
                              { label: "Idle", val: fmtMins(emp.idle_mins), c: "var(--gt-away)" },
                              { label: "Efficiency", val: `${eff.toFixed(0)}%`, c: "var(--gt-primary)" },
                            ].map((s) => (
                              <div key={s.label} style={{ background: "var(--gt-surface-2)", borderRadius: 12, padding: 12, textAlign: "center" }}>
                                <p style={{ fontSize: 10, color: "var(--gt-text-tertiary)" }}>{s.label}</p>
                                <p style={{ fontSize: 14, fontWeight: 700, color: s.c, marginTop: 2 }}>{s.val}</p>
                              </div>
                            ))}
                          </div>
                          <p style={{ fontSize: 10, fontWeight: 600, color: "var(--gt-text-tertiary)", marginBottom: 8 }}>Sessions</p>
                          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            {emp.data.map((s) => (
                              <div
                                key={s.id}
                                style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "8px 12px", background: "var(--gt-surface-2)", borderRadius: 10 }}
                              >
                                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--gt-text-secondary)" }}>
                                  <Calendar style={{ width: 11, height: 11, color: "var(--gt-text-tertiary)" }} />
                                  <span>{fmtDate(s.date)}</span>
                                  <span style={{ color: "var(--gt-text-disabled)" }}>·</span>
                                  <span style={{ fontFamily: "monospace" }}>{fmtTime(s.first_activity)}</span>
                                  <span>→</span>
                                  <span style={{ fontFamily: "monospace" }}>{fmtTime(s.last_activity)}</span>
                                </div>
                                <div style={{ display: "flex", gap: 12 }}>
                                  <span style={{ fontSize: 11, fontWeight: 500, color: "var(--gt-online)" }}>{fmtMins(s.active_mins)}</span>
                                  <span style={{ fontSize: 11, fontWeight: 500, color: "var(--gt-away)" }}>{fmtMins(s.idle_mins)}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityDashboard;
